import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, Loader2, Search, MapPin, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = "/api";

const StoreLocationsPage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    full_address: "",
    latitude: "",
    longitude: ""
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` }
  });

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/stores`, getAuthHeaders());
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      full_address: "",
      latitude: "",
      longitude: ""
    });
    setSelectedLocation(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      email: location.email,
      phone: location.phone,
      full_address: location.full_address,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString()
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude)
    };

    try {
      if (selectedLocation) {
        await axios.put(`${API}/stores/${selectedLocation.id}`, payload, getAuthHeaders());
        toast.success("Store location updated successfully");
      } else {
        await axios.post(`${API}/stores`, payload, getAuthHeaders());
        toast.success("Store location added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/stores/${selectedLocation.id}`, getAuthHeaders());
      toast.success("Store location deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete store");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLocations = locations.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.phone.includes(searchTerm)
  );

  return (
    <div data-testid="stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MapPin className="text-[#6C42F5]" size={28} />
            Stores
          </h1>
          <p className="text-gray-600 mt-1">Manage your store network</p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF] rounded-lg glow-primary"
          data-testid="add-store-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Store
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-store-locations"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6C42F5]" size={32} />
          </div>
        ) : filteredLocations.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No stores found matching your search" : "No stores added yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700">Name</TableHead>
                  <TableHead className="font-bold text-gray-700">Email</TableHead>
                  <TableHead className="font-bold text-gray-700">Phone</TableHead>
                  <TableHead className="font-bold text-gray-700">Address</TableHead>
                  <TableHead className="font-bold text-gray-700">Coordinates</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.map((location) => (
                  <TableRow key={location.id} className="hover:bg-gray-50" data-testid={`store-location-row-${location.id}`}>
                    <TableCell className="font-medium text-gray-900">{location.name}</TableCell>
                    <TableCell className="text-gray-600">{location.email}</TableCell>
                    <TableCell className="text-gray-600">{location.phone}</TableCell>
                    <TableCell className="text-gray-600 max-w-xs truncate">{location.full_address}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/stores/${location.id}`)}
                          className="text-gray-600 hover:text-[#6C42F5]"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(location)}
                          className="text-gray-600 hover:text-[#6C42F5]"
                          data-testid={`edit-store-location-${location.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(location)}
                          className="text-gray-600 hover:text-red-500"
                          data-testid={`delete-store-location-${location.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {selectedLocation ? "Edit Store" : "Add New Store"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Store location name"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="email@example.com"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-700 font-medium">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 98765 43210"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-phone"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="full_address" className="text-gray-700 font-medium">Full Address</Label>
                <Input
                  id="full_address"
                  name="full_address"
                  value={formData.full_address}
                  onChange={handleChange}
                  required
                  placeholder="123 Street, City, State, PIN"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-address"
                />
              </div>
              <div>
                <Label htmlFor="latitude" className="text-gray-700 font-medium">Latitude</Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={handleChange}
                  required
                  placeholder="19.0760"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-latitude"
                />
              </div>
              <div>
                <Label htmlFor="longitude" className="text-gray-700 font-medium">Longitude</Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={handleChange}
                  required
                  placeholder="72.8777"
                  className="mt-1 bg-white border-gray-300"
                  data-testid="input-store-location-longitude"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF]"
                data-testid="submit-store-location-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {selectedLocation ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  selectedLocation ? "Update Store" : "Add Store"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Delete Store</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to delete <span className="font-semibold">{selectedLocation?.name}</span>? 
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-300 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreLocationsPage;
